import React, { useState, useEffect, useRef } from 'react';
import { X, DoorOpen, Volume2, VolumeX, Loader2, CheckCircle, Phone, Maximize2, Minimize2 } from 'lucide-react';
import useIntercomStore from '../stores/intercomStore';
import WebRTCPlayer from './WebRTCPlayer';

export function IntercomAnswerModal() {
  const { activeCall, clearActiveCall, markAnswered } = useIntercomStore();
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [unlockSuccess, setUnlockSuccess] = useState(false);
  const [unlockError, setUnlockError] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const modalRef = useRef(null);

  useEffect(() => {
    if (activeCall) {
      fetch(`/api/intercom/notifications/${activeCall.id}/answer`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ answeredBy: 'user' }) }).catch(console.error);
      markAnswered(activeCall.id, 'you');
    }
  }, [activeCall?.id, markAnswered]);

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') handleClose(); };
    if (activeCall) { document.addEventListener('keydown', handleEscape); return () => document.removeEventListener('keydown', handleEscape); }
  }, [activeCall]);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  if (!activeCall) return null;

  const handleClose = () => { clearActiveCall(); setVideoReady(false); setUnlockSuccess(false); setUnlockError(null); };

  const handleUnlock = async () => {
    setIsUnlocking(true); setUnlockError(null);
    try {
      const response = await fetch(`/api/intercom/${activeCall.intercomId}/unlock`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' } });
      const data = await response.json();
      if (response.ok && data.success) { setUnlockSuccess(true); setTimeout(() => setUnlockSuccess(false), 3000); }
      else { setUnlockError(data.error || 'Failed to unlock'); }
    } catch (error) { setUnlockError('Network error'); }
    finally { setIsUnlocking(false); }
  };

  const toggleFullscreen = async () => {
    try { if (!document.fullscreenElement) await modalRef.current?.requestFullscreen(); else await document.exitFullscreen(); } catch (e) { console.error(e); }
  };

  const timeSince = () => { const s = Math.floor((Date.now() - new Date(activeCall.timestamp).getTime()) / 1000); return s < 60 ? `${s}s` : `${Math.floor(s/60)}m ${s%60}s`; };

  return (
    <div ref={modalRef} className="fixed inset-0 bg-black/95 z-50 flex flex-col">
      <div className="flex items-center justify-between p-4 bg-gray-900/80">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center"><Phone className="w-5 h-5 text-green-400" /></div>
          <div>
            <h2 className="text-white text-lg font-semibold">{activeCall.intercomName}</h2>
            <p className="text-gray-400 text-sm flex items-center gap-2"><span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />Connected • {timeSince()}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={toggleFullscreen} className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg">{isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}</button>
          <button onClick={handleClose} className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg"><X className="w-6 h-6" /></button>
        </div>
      </div>

      <div className="flex-1 relative bg-black flex items-center justify-center">
        {activeCall.cameraId ? (
          <>
            <WebRTCPlayer cameraId={activeCall.cameraId} autoPlay={true} muted={isMuted} onReady={() => setVideoReady(true)} className="max-h-full max-w-full object-contain" />
            {!videoReady && <div className="absolute inset-0 flex items-center justify-center bg-gray-900"><Loader2 className="w-12 h-12 text-cyan-500 animate-spin" /><p className="text-gray-400 ml-3">Connecting...</p></div>}
            <button onClick={() => setIsMuted(!isMuted)} className="absolute bottom-4 left-4 bg-black/60 hover:bg-black/80 p-3 rounded-full">{isMuted ? <VolumeX className="w-6 h-6 text-white" /> : <Volume2 className="w-6 h-6 text-white" />}</button>
          </>
        ) : <div className="text-center text-gray-400"><Phone className="w-16 h-16 mx-auto mb-4 opacity-50" /><p>No camera configured</p></div>}
      </div>

      <div className="p-4 bg-gray-900/80">
        {unlockError && <div className="mb-3 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm text-center">{unlockError}</div>}
        <div className="flex gap-3 max-w-lg mx-auto">
          <button onClick={handleUnlock} disabled={isUnlocking} className={`flex-1 flex items-center justify-center gap-3 py-4 px-6 rounded-xl font-semibold text-lg ${unlockSuccess ? 'bg-green-600' : 'bg-cyan-600 hover:bg-cyan-500'} text-white disabled:opacity-50`}>
            {isUnlocking ? <><Loader2 className="w-6 h-6 animate-spin" />Unlocking...</> : unlockSuccess ? <><CheckCircle className="w-6 h-6" />Unlocked!</> : <><DoorOpen className="w-6 h-6" />Unlock Door</>}
          </button>
          <button onClick={handleClose} className="px-6 py-4 bg-red-600 hover:bg-red-500 text-white rounded-xl font-semibold">End</button>
        </div>
      </div>
    </div>
  );
}

export default IntercomAnswerModal;
